import { useTranslation } from 'react-i18next'

import formatDefault from '@dentalux/common/date/formatDefault'

import { combinedFields } from '@ana/shared'
import { FieldSettings } from '@ana/shared'
import { Grid } from '@mui/material'

import { isDateFormatted } from '../Content/Content.helpers'
import Group from '../Group'
import PreviewField, { PreviewFieldProps } from '../PreviewField/PreviewField'
import Title from '../Title'

import { StyledGridStreet, StyledGridEmail } from './GeneralInformation.styles'

type Props = {
  getEditableFieldProps: (settings: Omit<FieldSettings, '__type'>) => Omit<PreviewFieldProps, 'transform'>
}

const SummaryGeneralInformation = ({ getEditableFieldProps }: Props) => {
  const { t } = useTranslation()

  const genderFieldProps = getEditableFieldProps(combinedFields.gender)
  const careLevelFieldProps = getEditableFieldProps(combinedFields.hasCareLevel)
  const entitledToIntegrationAssistanceProps = getEditableFieldProps(combinedFields.entitledToIntegrationAssistance)
  const integrationAssistanceBasedOnTypeProps = getEditableFieldProps(combinedFields.integrationAssistanceBasedOnType)

  return (
    <>
      <Title>{t('category.general_information')}</Title>

      <Group>
        <Grid size={{ xs: 6, md: 3 }}>
          <PreviewField
            {...genderFieldProps}
            transform={(value) => {
              return t(`option.${value?.toString()?.toLowerCase()}`, { defaultValue: genderFieldProps.value })
            }}
          />
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <PreviewField {...getEditableFieldProps(combinedFields.firstName)} />
        </Grid>

        <Grid size={{ xs: 6, md: 3 }}>
          <PreviewField {...getEditableFieldProps(combinedFields.lastName)} />
        </Grid>

        <Grid size={{ xs: 6, md: 3 }}>
          <PreviewField
            {...getEditableFieldProps(combinedFields.birthday)}
            transform={(value: string | boolean | number) =>
              isDateFormatted(value.toString()) ? value : formatDefault(value.toString())
            }
          />
        </Grid>

        <StyledGridStreet size={{ xs: 12, md: 6 }}>
          <PreviewField {...getEditableFieldProps(combinedFields.street)} />
        </StyledGridStreet>

        <Grid size={{ xs: 6, md: 3 }}>
          <PreviewField {...getEditableFieldProps(combinedFields.zipCode)} />
        </Grid>

        <Grid size={{ xs: 6, md: 3 }}>
          <PreviewField {...getEditableFieldProps(combinedFields.city)} />
        </Grid>

        <StyledGridEmail size={{ xs: 12, md: 6 }}>
          <PreviewField {...getEditableFieldProps(combinedFields.email)} />
        </StyledGridEmail>

        <Grid size={{ xs: 12, md: 6 }}>
          <PreviewField {...getEditableFieldProps(combinedFields.phone)} />
        </Grid>

        <Grid size={{ xs: 12, md: 12 }}>
          <PreviewField {...getEditableFieldProps(combinedFields.placeOfBirth)} />
        </Grid>
      </Group>

      <Group>
        <Grid size={{ xs: 6, md: 6 }}>
          <PreviewField {...careLevelFieldProps} />
        </Grid>

        {careLevelFieldProps.value && (
          <Grid size={{ xs: 6, md: 6 }}>
            <PreviewField {...getEditableFieldProps(combinedFields.careLevelLimitedInTime)} />
          </Grid>
        )}

        <Grid size={{ xs: 6, md: 6 }}>
          <PreviewField {...entitledToIntegrationAssistanceProps} />
        </Grid>

        {entitledToIntegrationAssistanceProps.value && (
          <Grid size={{ xs: 6, md: 6 }}>
            <PreviewField
              {...integrationAssistanceBasedOnTypeProps}
              transform={(value) => {
                return t(`option.${value?.toString()?.toLowerCase()}`, {
                  defaultValue: integrationAssistanceBasedOnTypeProps.value,
                })
              }}
            />
          </Grid>
        )}

        <Grid size={{ xs: 12, md: 6 }}>
          <PreviewField
            {...getEditableFieldProps(combinedFields.type)}
            transform={(value) => t(`option.${value?.toString().toLowerCase()}`, { defaultProps: value })}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <PreviewField {...getEditableFieldProps(combinedFields.hasCivilServiceAllowance)} />
        </Grid>

        <Grid size={{ xs: 6, md: 6 }}>
          <PreviewField {...getEditableFieldProps(combinedFields.insuranceCompany)} />
        </Grid>

        <Grid size={{ xs: 6, md: 6 }}>
          <PreviewField {...getEditableFieldProps(combinedFields.dentalInsuranceCompany)} />
        </Grid>

        <Grid size={{ xs: 6, md: 3 }}>
          <PreviewField {...getEditableFieldProps(combinedFields.hasFamilyInsurance)} />
        </Grid>

        <Grid size={{ xs: 6, md: 3 }}>
          <PreviewField {...getEditableFieldProps(combinedFields.holderFirstName)} />
        </Grid>

        <Grid size={{ xs: 6, md: 3 }}>
          <PreviewField {...getEditableFieldProps(combinedFields.holderLastName)} />
        </Grid>

        <Grid size={{ xs: 6, md: 3 }}>
          <PreviewField
            {...getEditableFieldProps(combinedFields.holderBirthday)}
            transform={(value) => (isDateFormatted(value.toString()) ? value : formatDefault(value.toString()))}
          />
        </Grid>

        <Grid size={{ xs: 6, md: 6 }}>
          <PreviewField {...getEditableFieldProps(combinedFields.holderStreet)} />
        </Grid>

        <Grid size={{ xs: 6, md: 3 }}>
          <PreviewField {...getEditableFieldProps(combinedFields.holderZipcode)} />
        </Grid>

        <Grid size={{ xs: 6, md: 3 }}>
          <PreviewField {...getEditableFieldProps(combinedFields.holderCity)} />
        </Grid>
      </Group>

      <Group>
        <Grid size={{ xs: 12, md: 12 }}>
          <PreviewField {...getEditableFieldProps(combinedFields.occupation)} />
        </Grid>
      </Group>

      <Group>
        <Grid size={{ xs: 6, md: 6 }}>
          <PreviewField {...getEditableFieldProps(combinedFields.isPregnant)} />
        </Grid>

        <Grid size={{ xs: 6, md: 6 }}>
          <PreviewField {...getEditableFieldProps(combinedFields.pregnantMonths)} />
        </Grid>
      </Group>

      <Group>
        <Grid size={{ xs: 6, md: 3 }}>
          <PreviewField
            {...getEditableFieldProps(combinedFields.alcoholConsumption)}
            transform={(val) =>
              t(`${combinedFields.alcoholConsumption.options.find((option) => option.value === val)?.label}`)
            }
          />
        </Grid>

        <Grid size={{ xs: 6, md: 3 }}>
          <PreviewField
            {...getEditableFieldProps(combinedFields.drugConsumption)}
            transform={(val) =>
              t(`${combinedFields.drugConsumption.options.find((option) => option.value === val)?.label}`)
            }
          />
        </Grid>

        <Grid size={{ xs: 6, md: 3 }}>
          <PreviewField
            {...getEditableFieldProps(combinedFields.cigaretteConsumption)}
            transform={(val) =>
              t(`${combinedFields.cigaretteConsumption.options.find((option) => option.value === val)?.label}`)
            }
          />
        </Grid>
      </Group>
    </>
  )
}

export default SummaryGeneralInformation
